/**
 * Database schema editor — used in the New Database dialog and the Edit Schema dialog.
 * Lets the user define an ordered list of columns, each with name + type (+ options for select).
 */
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";

export const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "longtext", label: "Long text" },
  { value: "date", label: "Date" },
  { value: "reminder", label: "Reminder (date)" },
  { value: "number", label: "Number" },
  { value: "url", label: "URL" },
  { value: "boolean", label: "Yes / No" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "select", label: "Single select" },
  { value: "multiselect", label: "Multi select" },
];

const slug = (s) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 40) || `field_${Math.random().toString(36).slice(2, 6)}`;

export const newColumn = (label = "") => ({
  key: slug(label),
  label,
  type: "text",
  options: [],
});

export default function DatabaseSchemaEditor({ columns, onChange }) {
  const update = (i, patch) => {
    const next = columns.map((c, idx) => (idx === i ? { ...c, ...patch } : c));
    onChange(next);
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= columns.length) return;
    const next = [...columns];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const remove = (i) => onChange(columns.filter((_, idx) => idx !== i));
  const add = () => onChange([...columns, newColumn("")]);

  const handleLabelChange = (i, label) => {
    const c = columns[i];
    // Only auto-update key while it's still empty or matches the previous slug.
    const prevSlug = slug(c.label);
    const next = { label };
    if (!c.key || c.key === prevSlug) next.key = slug(label);
    update(i, next);
  };

  return (
    <div data-testid="db-schema-editor">
      <div className="gc-label mb-2">Fields ({columns.length})</div>
      <div className="space-y-2">
        {columns.length === 0 && (
          <div className="text-xs text-slate-400 border border-dashed border-slate-200 px-3 py-3 rounded-sm">
            No fields yet. Add a field below to start.
          </div>
        )}
        {columns.map((c, i) => (
          <div
            key={i}
            className="flex flex-wrap items-start gap-2 p-2 border border-slate-200 rounded-sm"
            data-testid={`schema-row-${i}`}
          >
            <div className="flex flex-col">
              <Input
                placeholder="Field name (e.g., Shareholder Name)"
                value={c.label}
                onChange={(e) => handleLabelChange(i, e.target.value)}
                className="rounded-sm w-56"
                data-testid={`schema-label-${i}`}
              />
              <span className="mt-0.5 font-mono-data text-[10px] text-slate-400 px-1">
                key: {c.key || "—"}
              </span>
            </div>
            <Select value={c.type} onValueChange={(v) => update(i, { type: v, options: c.options || [] })}>
              <SelectTrigger className="w-40 rounded-sm" data-testid={`schema-type-${i}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FIELD_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(c.type === "select" || c.type === "multiselect") && (
              <Input
                placeholder="Options (comma-separated)"
                value={(c.options || []).join(", ")}
                onChange={(e) =>
                  update(i, {
                    options: e.target.value.split(",").map((o) => o.trim()).filter(Boolean),
                  })
                }
                className="rounded-sm flex-1 min-w-[180px]"
                data-testid={`schema-options-${i}`}
              />
            )}
            <div className="ml-auto inline-flex items-center gap-0.5">
              <button onClick={() => move(i, -1)} className="p-1.5 text-slate-400 hover:text-[#0a2540]" title="Move up" data-testid={`schema-up-${i}`}>
                <ArrowUp size={12} />
              </button>
              <button onClick={() => move(i, 1)} className="p-1.5 text-slate-400 hover:text-[#0a2540]" title="Move down" data-testid={`schema-down-${i}`}>
                <ArrowDown size={12} />
              </button>
              <button onClick={() => remove(i)} className="p-1.5 text-slate-400 hover:text-red-600" title="Remove" data-testid={`schema-remove-${i}`}>
                <Trash2 size={12} />
              </button>
            </div>
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" className="mt-3 rounded-sm" onClick={add} data-testid="btn-schema-add">
        <Plus size={12} className="mr-1" /> Add field
      </Button>
    </div>
  );
}
